/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	CircularProgress,
	Divider,
	FormControl,
	FormControlLabel,
	FormHelperText,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Switch,
	TextField,
	Typography,
	Paper,
	Checkbox,
	Autocomplete
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import Listboxes from "../ListBoxes/ListBoxes";
import { Delete, VpnKeyRounded } from "@mui/icons-material";
import ResetPasswordModal from "../ResetPasswordModal/ResetPasswordModal";
import { useAuth } from "../../context/authContext";
import { useSite } from "../../context/siteContext";
import { getRoles } from "../../services/createEditUSerFormService";
import { createNewUser } from "../../services/newUserService";
import { checkUserEmailExists, updateUser } from "../../services/userService";
import { getUserAllocations } from "../../services/manageSitesService";
import { styled } from "@mui/material/styles";
import PhoneInput from "../PhoneInput/PhoneInput";
import CountryAutocomplete from "../CountryAutocomplete/CountryAutocomplete";
import {
	getNationalDigits,
	getPhoneMetaForCountry,
	normalizePhoneForCountry,
	resolvePhoneCountry,
	resolvePhoneState,
} from "../../utilities/phoneUtils";
import { COUNTRIES_BY_REGION, REGION_OPTIONS } from "../../data/countryRegions";

const GroupHeader = styled("div")(() => ({
	position: "sticky",
	top: 0,
	padding: "4px 8px",
	color: "#181818",
	backgroundColor: "#eee",
	fontWeight: 600,
	fontSize: "12.5px",
}));

const GroupItems = styled("ul")({
	padding: 0,
	margin: 0,
	listStyle: "none",
});

// ---------------------------------------------------------------------------
// Helper: group flat { cus_id, site_id } pairs into ListBoxes format
// { customer: cusId, sites: [siteId, ...] }
// Handles the case where cus_id is null by looking it up from allSites.
// ---------------------------------------------------------------------------
const groupAllocationsForListBoxes = (flatAllocations = [], allSites = []) => {
	if (!Array.isArray(flatAllocations) || flatAllocations.length === 0) return [];

	// Build a fast site_id → cus_id lookup from the full sites list
	const siteToCusMap = new Map();
	(allSites || []).forEach((site) => {
		if (site?.site_id != null && site?.cus_id != null) {
			const siteId = Number(site.site_id);
			if (!siteToCusMap.has(siteId)) {
				siteToCusMap.set(siteId, Number(site.cus_id));
			}
		}
	});

	const map = new Map(); // cusId (number) → { customer: cusId, sites: [] }

	flatAllocations.forEach((entry) => {
		const siteId = Number(entry?.site_id);
		if (!Number.isFinite(siteId) || siteId <= 0) return;

		// Prefer the allocation's own cus_id; fall back to lookup from allSites
		let cusId = Number(entry?.cus_id);
		if (!Number.isFinite(cusId) || cusId <= 0) {
			cusId = siteToCusMap.get(siteId) ?? null;
		}

		if (!cusId || !Number.isFinite(cusId) || cusId <= 0) return; // still can't resolve

		if (!map.has(cusId)) {
			map.set(cusId, { customer: cusId, sites: [] });
		}
		map.get(cusId).sites.push(siteId);
	});

	return Array.from(map.values());
};

const initializeLocalState = (userData, isEdit) => {
	const sourceCountry = userData?.country || userData?.user_meta?.country || "";
	const phoneState = resolvePhoneState({
		phoneValue: userData?.user_phone || "",
		countryLabel: sourceCountry,
		phoneCountryIso2: userData?.user_meta?.phone_country_iso2 || "",
		phoneCountryCode: userData?.user_meta?.phone_country_code || "",
	});
	return {
		user_first_name: userData?.user_first_name || "",
		user_last_name: userData?.user_last_name || "",
		user_email: userData?.user_email || "",
		user_phone: phoneState.phoneValue,
		user_role: userData?.user_role || "",
		user_status: userData?.user_status?.toString?.() || (isEdit ? "0" : "0"),
		company_name: userData?.company_name || userData?.user_meta?.company_name || "",
		region: userData?.region || userData?.user_meta?.region || "",
		country: sourceCountry,
		approved_domains: userData?.approved_domains || userData?.user_meta?.approved_domains || "",
		phone_country_code: phoneState.meta.phone_country_code,
		phone_country_iso2: phoneState.meta.phone_country_iso2,
		phone_country_flag: phoneState.meta.phone_country_flag,
		notify_order_creations_and_requests: (function () {
			const v = userData?.user_meta?.notify_order_creations_and_requests;
			if (v === true) return true;
			if (typeof v === "string") {
				const s = v.toLowerCase();
				return s === "true" || s === "1" || s === "yes" || s === "on";
			}
			return false;
		})(),
	};
};



const CreateEditUserForm = ({
	mode = 'create',
	userData,
	setUserData,
	allCustomers,
	allSites,
	assignedSitesLoc,
	onAssignedSitesChange,
	onClose,
	onSaved,
	initialPwdModalOpen = false,
	contref = null
}) => {
	const { t } = useTranslation();
	const isEdit = mode === 'edit';
	const { user: authUser, updateUserStatus } = useAuth();
	const { fetchSites } = useSite();
	const isSelf = isEdit && authUser?.user_id === userData?.user_id;

	const [loading, setLoading] = useState(false);
	const [roles, setRoles] = useState([]);
	const [rolesLoading, setRolesLoading] = useState(true);
	const [selectedRole, setSelectedRole] = useState(null);
	const [errors, setErrors] = useState({});
	const [local, setLocal] = useState(() => initializeLocalState(userData, isEdit));
	const [sendPasswordEmail, setSendPasswordEmail] = useState(true);
	const [pwdModalOpen, setPwdModalOpen] = useState(false);
	const [sendResetLink, setSendResetLink] = useState(true);
	const [companyExtras, setCompanyExtras] = useState({ stateProvince: '', streetAddress: '', city: '', zipPostalCode: '' });
	const [approvedDomainsInputError, setApprovedDomainsInputError] = useState('');
	const [fallbackActorSiteIds, setFallbackActorSiteIds] = useState([]);
	const [actorScopeLoading, setActorScopeLoading] = useState(false);
	const [passwords, setPasswords] = useState({ pass: '', cpass: '' });
	const [pwdValidation, setPwdValidation] = useState({ minLength: false, uppercase: false, specialChar: false, number: false, confirmpass: false });
	const [emailExists, setEmailExists] = useState(false);
	const [emailCheckInProgress, setEmailCheckInProgress] = useState(false);

	const countries = useMemo(() => COUNTRIES_BY_REGION[local.region] || [], [local.region]);
	const groupedRoleOptions = useMemo(() => {
		const arr = Array.isArray(roles) ? [...roles] : [];
		arr.sort((a, b) => {
			const ta = String(a?.role_type || "Other");
			const tb = String(b?.role_type || "Other");
			if (ta !== tb) return ta.localeCompare(tb);
			return String(a?.role_name || "").localeCompare(String(b?.role_name || ""));
		});
		return arr;
	}, [roles]);
	const actorUserManagementPerms = useMemo(() => authUser?.role?.permissions?.user_management || {}, [authUser?.role?.permissions]);
	const actorRolePermissions = useMemo(() => authUser?.role?.permissions || {}, [authUser?.role?.permissions]);
	const actorRoleIsAdmin = String(authUser?.role?.role_type || authUser?.role?.role_name || "").trim().toLowerCase() === "admin";
	const toBool = (value, fallback = false) => {
		if (typeof value === "boolean") return value;
		if (typeof value === "number") return value === 1;
		if (typeof value === "string") {
			const normalized = value.trim().toLowerCase();
			if (["true", "1", "yes", "on", "enabled"].includes(normalized)) return true;
			if (["false", "0", "no", "off", "disabled"].includes(normalized)) return false;
		}
		return fallback;
	};
	const normalizePermissionsObject = (permissions) => {
		if (!permissions) return {};
		if (typeof permissions === "string") {
			try {
				const parsed = JSON.parse(permissions);
				return parsed && typeof parsed === "object" ? parsed : {};
			} catch (_) {
				return {};
			}
		}
		return permissions && typeof permissions === "object" ? permissions : {};
	};
	const actorAssignableRoleTypes = useMemo(() => {
		if (authUser?.user_id === 1) return ['admin', 'user'];
		const raw = Array.isArray(actorUserManagementPerms?.assignable_role_types)
			? actorUserManagementPerms.assignable_role_types
			: ['User'];
		return raw.map((v) => String(v || '').trim().toLowerCase()).filter(Boolean);
	}, [actorUserManagementPerms?.assignable_role_types, authUser?.user_id]);

	const actorSiteIds = useMemo(() => {
		const fromAllocs = Array.isArray(authUser?.allocations)
			? authUser.allocations.map((a) => Number(a?.site_id)).filter((id) => Number.isFinite(id) && id > 0)
			: [];
		return Array.from(new Set(fromAllocs));
	}, [authUser?.allocations]);
	useEffect(() => {
		let mounted = true;
		const loadFallbackActorScope = async () => {
			if (authUser?.user_id === 1 || actorSiteIds.length > 0 || !authUser?.user_id) {
				if (mounted) {
					setFallbackActorSiteIds([]);
					setActorScopeLoading(false);
				}
				return;
			}
			setActorScopeLoading(true);
			try {
				const resp = await getUserAllocations(authUser.user_id);
				const allocations = Array.isArray(resp?.data?.allocations) ? resp.data.allocations : [];
				const ids = Array.from(
					new Set(
						allocations
							.map((a) => Number(a?.site_id))
							.filter((id) => Number.isFinite(id) && id > 0)
					)
				);
				if (mounted) setFallbackActorSiteIds(ids);
			} catch (error) {
				console.error("Failed to load actor allocations for scope filtering", error);
				if (mounted) setFallbackActorSiteIds([]);
			} finally {
				if (mounted) setActorScopeLoading(false);
			}
		};
		loadFallbackActorScope();
		return () => { mounted = false; };
	}, [actorSiteIds, authUser?.user_id]);

	const effectiveActorSiteIds = useMemo(() => {
		if (actorSiteIds.length > 0) return actorSiteIds;
		return fallbackActorSiteIds;
	}, [actorSiteIds, fallbackActorSiteIds]);
	const actorManageAllSites =
		authUser?.user_id === 1 ||
		((actorRoleIsAdmin || toBool(actorUserManagementPerms?.manage_all_sites, false)) && effectiveActorSiteIds.length === 0);

	const scopedSites = useMemo(() => {
		if (actorManageAllSites) return allSites;
		if (effectiveActorSiteIds.length === 0) return [];
		const set = new Set(effectiveActorSiteIds.map((id) => Number(id)));
		return (allSites || []).filter((site) => set.has(Number(site?.site_id)));
	}, [actorManageAllSites, effectiveActorSiteIds, allSites]);

	const scopedCustomers = useMemo(() => {
		if (actorManageAllSites) return allCustomers;
		if (effectiveActorSiteIds.length === 0) return [];
		const customerIds = new Set((scopedSites || []).map((s) => Number(s?.cus_id)).filter((id) => Number.isFinite(id) && id > 0));
		return (allCustomers || []).filter((c) => customerIds.has(Number(c?.cus_id)));
	}, [actorManageAllSites, effectiveActorSiteIds, scopedSites, allCustomers]);

	const assignableRoleOptions = useMemo(() => {
		if (authUser?.user_id === 1) return groupedRoleOptions;
		const toScopeLevel = (scopeValue = 'created_by_me') => {
			const normalized = String(scopeValue || '').trim().toLowerCase();
			return normalized === 'all' ? 2 : 1;
		};
		const toDomainScopeLevel = (scopeValue = 'approved_domains_only') => {
			const normalized = String(scopeValue || '').trim().toLowerCase();
			return normalized === 'any_domain' ? 2 : 1;
		};
		const toSiteAllocationScopeLevel = (scopeValue = 'specific_only') => {
			const normalized = String(scopeValue || '').trim().toLowerCase();
			return normalized === 'all_or_specific' ? 2 : 1;
		};
		const restrictsWhenTrue = new Set([
			'user_management.restrict_email_to_approved_domains',
		]);
		const hasTargetBooleanOutsideActor = (targetNode, actorNode, pathPrefix = '') => {
			if (!targetNode || typeof targetNode !== 'object') return false;
			return Object.keys(targetNode).some((key) => {
				const path = pathPrefix ? `${pathPrefix}.${key}` : key;
				const targetValue = targetNode[key];
				const actorValue = actorNode && typeof actorNode === 'object' ? actorNode[key] : undefined;
				if (typeof targetValue === 'boolean') {
					if (restrictsWhenTrue.has(path)) {
						// Reverse semantics for restrictive booleans:
						// false is broader than true.
						return targetValue === false && actorValue === true;
					}
					return targetValue === true && actorValue !== true;
				}
				if (targetValue && typeof targetValue === 'object' && !Array.isArray(targetValue)) {
					return hasTargetBooleanOutsideActor(targetValue, actorValue, path);
				}
				return false;
			});
		};
		const canAssignRoleByPermission = (role) => {
			const roleType = String(role?.role_type || '').trim().toLowerCase();
			if (roleType !== 'admin') return true;
			const rolePerms = normalizePermissionsObject(role?.permissions);
			const actorPerms = actorRolePermissions && typeof actorRolePermissions === 'object' ? actorRolePermissions : {};
			const actorUserMgmt = actorPerms?.user_management || {};
			const roleUserMgmt = rolePerms?.user_management || {};
			const actorAssignable = Array.isArray(actorUserMgmt?.assignable_role_types)
				? actorUserMgmt.assignable_role_types.map((v) => String(v || '').trim().toLowerCase()).filter(Boolean)
				: ['user'];
			const roleAssignable = Array.isArray(roleUserMgmt?.assignable_role_types)
				? roleUserMgmt.assignable_role_types.map((v) => String(v || '').trim().toLowerCase()).filter(Boolean)
				: ['user'];
			if (roleAssignable.some((v) => !actorAssignable.includes(v))) return false;
			if (toScopeLevel(roleUserMgmt?.view_scope || 'all') > toScopeLevel(actorUserMgmt?.view_scope || 'all')) return false;
			if (toScopeLevel(roleUserMgmt?.edit_scope || 'created_by_me') > toScopeLevel(actorUserMgmt?.edit_scope || 'created_by_me')) return false;
			if (toScopeLevel(roleUserMgmt?.delete_scope || 'created_by_me') > toScopeLevel(actorUserMgmt?.delete_scope || 'created_by_me')) return false;
			if (toDomainScopeLevel(roleUserMgmt?.domain_user_management_scope || 'approved_domains_only') > toDomainScopeLevel(actorUserMgmt?.domain_user_management_scope || 'approved_domains_only')) return false;
			if (toSiteAllocationScopeLevel(roleUserMgmt?.site_allocation_scope || 'specific_only') > toSiteAllocationScopeLevel(actorUserMgmt?.site_allocation_scope || 'specific_only')) return false;
			if (hasTargetBooleanOutsideActor(rolePerms, actorPerms)) return false;
			return true;
		};
		return groupedRoleOptions.filter((role) => {
			const type = String(role?.role_type || '').trim().toLowerCase();
			const keepSelected = selectedRole && Number(selectedRole?.role_id) === Number(role?.role_id);
			return keepSelected || (actorAssignableRoleTypes.includes(type) && canAssignRoleByPermission(role));
		});
	}, [groupedRoleOptions, actorAssignableRoleTypes, authUser?.user_id, selectedRole, actorRolePermissions]);

	// Sync local form state when switching between users or modes
	useEffect(() => {
		const nextLocal = initializeLocalState(userData, isEdit);
		setLocal(nextLocal);
		setErrors({});
		setEmailExists(false);
		setEmailCheckInProgress(false);
		// reset password related local state when changing context
		setSendPasswordEmail(true);
		setPasswords({ pass: '', cpass: '' });
		setPwdValidation({ minLength: false, uppercase: false, specialChar: false, number: false, confirmpass: false });

		if (isEdit && userData?.user_meta) {
			setCompanyExtras({
				stateProvince: userData.user_meta.company_state_province || '',
				streetAddress: userData.user_meta.company_street_address || '',
				city: userData.user_meta.company_city || '',
				zipPostalCode: userData.user_meta.company_zip_postal_code || ''
			});
		} else {
			setCompanyExtras({ stateProvince: '', streetAddress: '', city: '', zipPostalCode: '' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEdit, userData?.user_id]);

	useEffect(() => {
		if (isEdit && userData?.user_meta) {
			setCompanyExtras({
				stateProvince: userData.user_meta.company_state_province || '',
				streetAddress: userData.user_meta.company_street_address || '',
				city: userData.user_meta.company_city || '',
				zipPostalCode: userData.user_meta.company_zip_postal_code || ''
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEdit, userData?.user_meta]);

	// Open password modal on demand (from grid icon)
	useEffect(() => {
		if (initialPwdModalOpen && isEdit) {
			setPwdModalOpen(true);
		}
	}, [initialPwdModalOpen, isEdit]);

	useEffect(() => {
		(async () => {
			setRolesLoading(true);
			try {
				const resp = await getRoles();
				if (resp?.status) {
					const payload = Array.isArray(resp?.data?.roles)
						? resp.data.roles
						: Array.isArray(resp?.data)
							? resp.data
							: [];
					setRoles(payload);
				}
			} catch (e) {
				console.error('Failed to load roles', e);
			} finally {
				setRolesLoading(false);
			}
		})();
	}, []);

	useEffect(() => {
		if (!local.user_role) { setSelectedRole(null); return; }
		const normalized = String(local.user_role).trim().toLowerCase();
		const sel =
			roles.find((r) => String(r.role_id) === String(local.user_role)) ||
			roles.find((r) => String(r.role_name || "").trim().toLowerCase() === normalized) ||
			roles.find((r) => String(r.role_type || "").trim().toLowerCase() === normalized) ||
			null;
		setSelectedRole(sel);
	}, [local.user_role, roles]);

	const selectedRoleName = String(selectedRole?.role_name || '').trim().toLowerCase();
	const selectedRoleType = String(selectedRole?.role_type || '').trim().toLowerCase();
	const selectedRoleIsAdminType = (selectedRoleName === 'admin' || selectedRoleType === 'admin');
	const selectedRolePermissions = useMemo(
		() => normalizePermissionsObject(selectedRole?.permissions),
		[selectedRole?.permissions]
	);
	const selectedRoleUserMgmt = useMemo(
		() =>
			selectedRolePermissions?.user_management ||
			selectedRolePermissions?.userManagement ||
			selectedRole?.user_management ||
			selectedRole?.userManagement ||
			{},
		[selectedRolePermissions, selectedRole]
	);
	const selectedRoleScopeRaw =
		selectedRoleUserMgmt?.site_allocation_scope ??
		selectedRoleUserMgmt?.siteAllocationScope ??
		selectedRolePermissions?.site_allocation_scope ??
		selectedRolePermissions?.siteAllocationScope ??
		selectedRole?.site_allocation_scope ??
		selectedRole?.siteAllocationScope;
	const selectedRoleManageAllSitesRaw =
		selectedRoleUserMgmt?.manage_all_sites ??
		selectedRoleUserMgmt?.manageAllSites ??
		selectedRolePermissions?.manage_all_sites ??
		selectedRolePermissions?.manageAllSites ??
		selectedRole?.manage_all_sites ??
		selectedRole?.manageAllSites;
	const normalizeSiteAllocationScope = (scopeValue) => {
		const normalized = String(scopeValue || "").trim().toLowerCase();
		if (!normalized) {
			// Safer default: explicit allocations unless role scope is explicitly all-or-specific.
			return "specific_only";
		}
		const isSpecificOnlyLabel =
			normalized === "specific_only" ||
			normalized === "only_specific_site" ||
			normalized === "only_specific_sites" ||
			normalized === "specific_site_only" ||
			normalized === "specific_sites_only" ||
			((normalized.includes("specific") || normalized.includes("spcific") || normalized.includes("only")) && !normalized.includes("all"));
		if (isSpecificOnlyLabel) return "specific_only";
		if (normalized.includes("all") || normalized === "all_or_specific") return "all_or_specific";
		// Unknown values should behave as specific-only.
		return "specific_only";
	};
	const selectedRoleSiteAllocationScope = normalizeSiteAllocationScope(
		selectedRoleScopeRaw
	);
	const selectedRoleAllowsAllSitesMode =
		selectedRoleSiteAllocationScope === "all_or_specific" &&
		toBool(selectedRoleManageAllSitesRaw, true);
	const selectedRoleDomainScope = String(
		selectedRoleUserMgmt?.domain_user_management_scope ||
		selectedRoleUserMgmt?.domainUserManagementScope ||
		'approved_domains_only'
	).trim().toLowerCase();

	// ── KEY FIX: buildExistingAllocations now resolves null cus_id from allSites ──
	const buildExistingAllocations = () => {
		// Build site_id → cus_id lookup from the full allSites array
		const siteToCusMap = new Map();
	(allSites || []).forEach((site) => {
		if (site?.site_id != null && site?.cus_id != null) {
			const siteId = Number(site.site_id);
			if (!siteToCusMap.has(siteId)) {
				siteToCusMap.set(siteId, Number(site.cus_id));
			}
		}
	});

		// Try userData.allocations first — resolve null cus_id via siteToCusMap
		if (Array.isArray(userData?.allocations) && userData.allocations.length > 0) {
			const resolved = userData.allocations
				.map((entry) => {
					const siteId = Number(entry?.site_id);
					if (!Number.isFinite(siteId) || siteId <= 0) return null;

					let cusId = Number(entry?.cus_id);
					// cus_id is null from server → look it up from allSites
					if (!Number.isFinite(cusId) || cusId <= 0) {
						cusId = siteToCusMap.get(siteId) ?? null;
					}
					if (!cusId || !Number.isFinite(cusId) || cusId <= 0) return null;

					return { cus_id: cusId, site_id: siteId };
				})
				.filter(Boolean);

			if (resolved.length > 0) return resolved;
		}

		// Fallback: user_sites array (just site IDs) → resolve cus_id from allSites
		const fallbackSiteIds = Array.isArray(userData?.user_sites)
			? userData.user_sites.map(Number).filter((id) => Number.isFinite(id) && id > 0)
			: String(userData?.user_sites || "")
				.split(",")
				.map((id) => Number(id))
				.filter((id) => Number.isFinite(id) && id > 0);

		return fallbackSiteIds
			.map((siteId) => {
				const cusId = siteToCusMap.get(siteId) ?? null;
				if (!cusId || !Number.isFinite(cusId) || cusId <= 0) return null;
				return { cus_id: cusId, site_id: siteId };
			})
			.filter(Boolean);
	};

	const existingAllocations = useMemo(
		buildExistingAllocations,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[userData?.allocations, userData?.user_sites, allSites]
	);
	const hasExistingAllocations = existingAllocations.length > 0;

	// ── KEY FIX: Convert flat allocations → grouped { customer, sites[] } format
	// for ListBoxes. This is what gets passed as assignedSitesProp.
	// The parent passes assignedSitesLoc which may be flat or grouped.
	// We expose a computed grouped version for the Listboxes component.
	const groupedAssignedSitesForListBoxes = useMemo(() => {
		if (!Array.isArray(assignedSitesLoc) || assignedSitesLoc.length === 0) return [];

		// Check if already in grouped format: { customer, sites: [] }
		const firstEntry = assignedSitesLoc[0];
		if (firstEntry && Array.isArray(firstEntry.sites)) {
			// Already grouped — pass through as-is
			return assignedSitesLoc;
		}

		// Flat format — group it, resolving null cus_id from allSites
		return groupAllocationsForListBoxes(assignedSitesLoc, allSites);
	}, [assignedSitesLoc, allSites]);

	const roleGuidanceText = useMemo(() => {
		if (!selectedRole) {
			return "Select a role to view a summary of access for this user.";
		}
		const roleSummary = selectedRoleIsAdminType
			? `${selectedRole?.role_name || "This role"} permissions: user creation is ${selectedRoleUserMgmt?.create_users ? "enabled" : "disabled"}, user editing is ${selectedRoleUserMgmt?.edit_users ? "enabled" : "disabled"}, and user deletion is ${selectedRoleUserMgmt?.delete_users ? "enabled" : "disabled"}.`
			: "";
		const siteSummary =
			selectedRoleAllowsAllSitesMode && actorManageAllSites
				? "Site allocation guidance: leave allocations empty to grant all-site access, or add specific sites to restrict access."
				: "Site allocation guidance: assign one or more specific sites for this user. Leaving allocations empty is not allowed.";
		return `${roleSummary} ${siteSummary}`.trim();
	}, [selectedRole, selectedRoleUserMgmt?.create_users, selectedRoleUserMgmt?.edit_users, selectedRoleUserMgmt?.delete_users, selectedRoleIsAdminType, selectedRoleAllowsAllSitesMode, actorManageAllSites]);

	const allocNoteText = useMemo(() => {
		if (selectedRoleAllowsAllSitesMode && actorManageAllSites) return "";
		return "Assign one or more customer-site allocations from your available scope. You cannot leave this empty.";
	}, [selectedRoleAllowsAllSitesMode, actorManageAllSites]);

	const getEmailDomain = (email) => `${email || ''}`.split('@').pop()?.toLowerCase() || '';
	const normalizeEmail = (value = "") => String(value || "").trim().toLowerCase();
	const isValidEmailFormat = (value = "") => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
	const normalizeDomainCsv = (raw) =>
		`${raw || ''}`
			.split(',')
			.map((d) => d.trim().replace(/^@/, '').toLowerCase())
			.filter(Boolean);
	const isValidDomain = (domain) =>
		/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i.test(`${domain || ''}`.trim());
	const creatorAllowedDomains = useMemo(() => {
		const domainScope = String(actorUserManagementPerms?.domain_user_management_scope || 'approved_domains_only').trim().toLowerCase();
		if (domainScope === 'any_domain') return [];
		const raw = `${authUser?.approved_domains || authUser?.user_meta?.approved_domains || ''}`.trim();
		if (!raw) return [];
		return raw.split(',').map(s => s.trim().replace(/^@/, '').toLowerCase()).filter(Boolean);
	}, [authUser?.approved_domains, authUser?.user_meta?.approved_domains, actorUserManagementPerms?.domain_user_management_scope]);
	const initialApprovedDomains = useMemo(
		() => normalizeDomainCsv(userData?.approved_domains || userData?.user_meta?.approved_domains || ''),
		[userData?.approved_domains, userData?.user_meta?.approved_domains]
	);
	const initialApprovedDomainSet = useMemo(
		() => new Set(initialApprovedDomains),
		[initialApprovedDomains]
	);
	const approvedDomainsFieldDisabled = true;
	const showApprovedDomainsInput = false;
	const actorHasRestrictedDomainScope =
		showApprovedDomainsInput &&
		Array.isArray(creatorAllowedDomains) &&
		creatorAllowedDomains.length > 0;
	const lockApprovedDomainsInput = !isEdit && actorHasRestrictedDomainScope;
	const approvedDomainRows = useMemo(() => {
		const raw = String(local?.approved_domains || '');
		const rows = raw.split(',').map((v) => v.trim());
		if (rows.length === 0) return [''];
		return rows;
	}, [local?.approved_domains]);
	const updateApprovedDomainsRows = (rows) => {
		setApprovedDomainsInputError('');
		setLocal((prev) => ({ ...prev, approved_domains: (rows || []).join(', ') }));
		if (errors.approved_domains) {
			setErrors((prev) => ({ ...prev, approved_domains: '' }));
		}
	};
	const handleApprovedDomainRowChange = (index, nextValue) => {
		if (String(nextValue || '').includes(',')) {
			setApprovedDomainsInputError('Enter only one domain per field. Use + to add another domain.');
			return;
		}
		const rows = [...approvedDomainRows];
		rows[index] = String(nextValue || '').trim().replace(/^@/, '').toLowerCase();
		updateApprovedDomainsRows(rows);
	};
	const handleAddApprovedDomainRow = () => {
		updateApprovedDomainsRows([...(approvedDomainRows || []), '']);
	};
	const handleRemoveApprovedDomainRow = (index) => {
		const rows = [...approvedDomainRows];
		const candidate = String(rows[index] || '').trim().toLowerCase();
		const isProtectedExistingOutOfScope =
			isEdit &&
			actorHasRestrictedDomainScope &&
			candidate &&
			!creatorAllowedDomains.includes(candidate) &&
			initialApprovedDomainSet.has(candidate);
		if (isProtectedExistingOutOfScope) {
			setApprovedDomainsInputError('This domain is outside your scope and cannot be removed.');
			return;
		}
		rows.splice(index, 1);
		updateApprovedDomainsRows(rows.length > 0 ? rows : ['']);
	};
	const showAllocateSpecificLocationsSection = selectedRoleAllowsAllSitesMode;
	const canUseGlobalAllSitesMode = selectedRoleAllowsAllSitesMode && actorManageAllSites;

	useEffect(() => {
		if (!isEdit) return;
		if (showApprovedDomainsInput) return;
		const existing = String(userData?.approved_domains || userData?.user_meta?.approved_domains || "").trim();
		if (!existing) return;
		if (String(local?.approved_domains || "").trim()) return;
		setLocal((prev) => ({ ...prev, approved_domains: existing }));
	}, [isEdit, showApprovedDomainsInput, userData?.approved_domains, userData?.user_meta?.approved_domains, local?.approved_domains]);

	const initialEmailNormalized = useMemo(() => normalizeEmail(userData?.user_email || ""), [userData?.user_email]);
	const currentEmailNormalized = useMemo(() => normalizeEmail(local?.user_email || ""), [local?.user_email]);

	useEffect(() => {
		let cancelled = false;
		setEmailExists(false);
		setEmailCheckInProgress(false);
		if (!currentEmailNormalized || !isValidEmailFormat(currentEmailNormalized)) return;
		if (isEdit && currentEmailNormalized === initialEmailNormalized) return;
		const timer = setTimeout(async () => {
			setEmailCheckInProgress(true);
			try {
				const resp = await checkUserEmailExists(currentEmailNormalized);
				const exists = Boolean(resp?.status && resp?.data?.exists === true);
				if (!cancelled) setEmailExists(exists);
			} catch (_err) {
				if (!cancelled) setEmailExists(false);
			} finally {
				if (!cancelled) setEmailCheckInProgress(false);
			}
		}, 350);
		return () => { cancelled = true; clearTimeout(timer); };
	}, [currentEmailNormalized, isEdit, initialEmailNormalized]);

	useEffect(() => {
		if (!isEdit) return;
		if (!showApprovedDomainsInput) return;
		const existingApprovedDomains = String(userData?.approved_domains || userData?.user_meta?.approved_domains || "").trim();
		if (!existingApprovedDomains) return;
		setLocal((prev) => {
			const current = String(prev?.approved_domains || '').trim();
			if (current) return prev;
			return { ...prev, approved_domains: existingApprovedDomains };
		});
	}, [isEdit, showApprovedDomainsInput, userData?.approved_domains, userData?.user_meta?.approved_domains]);
	useEffect(() => {
		if (isEdit) return;
		if (!showApprovedDomainsInput) return;
		if (!Array.isArray(creatorAllowedDomains) || creatorAllowedDomains.length === 0) return;
		if (String(local?.approved_domains || '').trim()) return;
		setLocal((prev) => ({ ...prev, approved_domains: creatorAllowedDomains.join(', ') }));
	}, [isEdit, showApprovedDomainsInput, creatorAllowedDomains, local?.approved_domains]);
	useEffect(() => {
		if (isEdit) return;
		if (!lockApprovedDomainsInput) return;
		const lockedValue = creatorAllowedDomains.join(', ');
		if (String(local?.approved_domains || '').trim() === lockedValue) return;
		setLocal((prev) => ({ ...prev, approved_domains: lockedValue }));
	}, [isEdit, lockApprovedDomainsInput, creatorAllowedDomains, local?.approved_domains]);

	const targetApprovedDomains = useMemo(() => normalizeDomainCsv(local.approved_domains), [local.approved_domains]);
	const approvedDomainsInvalidEntry = useMemo(
		() => targetApprovedDomains.find((d) => !isValidDomain(d)) || '',
		[targetApprovedDomains]
	);
	const approvedDomainsOutsideScope = useMemo(() => {
		if (creatorAllowedDomains.length === 0) return '';
		if (!isEdit) return targetApprovedDomains.find((d) => !creatorAllowedDomains.includes(d)) || '';
		return targetApprovedDomains.find((d) => !creatorAllowedDomains.includes(d) && !initialApprovedDomainSet.has(d)) || '';
	}, [targetApprovedDomains, creatorAllowedDomains, isEdit, initialApprovedDomainSet]);

	const approvedDomainsRealtimeError = useMemo(() => {
		if (approvedDomainsFieldDisabled) return '';
		if (!showApprovedDomainsInput) return '';
		if (approvedDomainsInputError) return approvedDomainsInputError;
		if (targetApprovedDomains.length === 0) return 'Approved domains are required for the selected role.';
		if (approvedDomainsInvalidEntry) return `Invalid domain: ${approvedDomainsInvalidEntry}`;
		if (approvedDomainsOutsideScope) return `Domain outside your allowed scope: ${approvedDomainsOutsideScope}`;
		return '';
	}, [showApprovedDomainsInput, approvedDomainsInputError, targetApprovedDomains, approvedDomainsInvalidEntry, approvedDomainsOutsideScope]);

	const approvedDomainsHelperText = useMemo(() => {
		if (!showApprovedDomainsInput) return '';
		if (approvedDomainsFieldDisabled) return 'Approved domains are currently disabled on this form.';
		if (lockApprovedDomainsInput) return `Prefilled from your allowed domains (${creatorAllowedDomains.join(', ')}). Domain changes are restricted.`;
		if (errors.approved_domains) return errors.approved_domains;
		if (approvedDomainsRealtimeError) return approvedDomainsRealtimeError;
		if (creatorAllowedDomains.length > 0) return `Allowed domains: ${creatorAllowedDomains.join(', ')}.`;
		return 'Enter one domain per field.';
	}, [showApprovedDomainsInput, lockApprovedDomainsInput, errors.approved_domains, approvedDomainsRealtimeError, creatorAllowedDomains]);

	const domainOk = approvedDomainsFieldDisabled || !showApprovedDomainsInput || !approvedDomainsRealtimeError;

	useEffect(() => {
		if (!canUseGlobalAllSitesMode) {
			if (local?._overrideAlloc) {
				setLocal((prev) => ({ ...prev, _overrideAlloc: false }));
			}
			return;
		}
		if (local?._overrideAlloc === undefined && isEdit && hasExistingAllocations) {
			setLocal((prev) => ({ ...prev, _overrideAlloc: true }));
			return;
		}
		if (local?._overrideAlloc === undefined && Array.isArray(assignedSitesLoc) && assignedSitesLoc.length > 0) {
			setLocal((prev) => ({ ...prev, _overrideAlloc: true }));
		}
	}, [canUseGlobalAllSitesMode, assignedSitesLoc, local?._overrideAlloc, isEdit, hasExistingAllocations]);

	const validate = () => {
		const e = {};
		if (!local.user_first_name.trim()) e.user_first_name = t('UI_TEXT.FIRST_NAME_IS_REQUIRED');
		if (!local.user_last_name.trim()) e.user_last_name = t('UI_TEXT.LAST_NAME_IS_REQUIRED');
		if (!local.user_email.trim()) e.user_email = t('ALERT_MESSAGES.EMAIL_IS_REQUIRED');
		else {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(local.user_email.trim())) e.user_email = t('GENERAL.INVALID_EMAIL_FORMAT');
		}
		if (emailExists) e.user_email = 'Email already exists. Please use a different email.';
		if (!local.company_name.trim()) e.company_name = t('GENERAL.COMPANY_NAME_IS_REQUIRED');
		if (!local.region.trim()) e.region = t('GENERAL.REGION_IS_REQUIRED');
		if (!local.country.trim()) e.country = t('GENERAL.COUNTRY_IS_REQUIRED');
		if (!local.user_role) e.user_role = t('GENERAL.ROLE_IS_REQUIRED');
		if (local.user_status === undefined || local.user_status === null || local.user_status === '') e.user_status = t('GENERAL.STATUS_IS_REQUIRED');
		const phoneDigits = getNationalDigits(local.user_phone || '', local.phone_country_iso2 || 'US');
		if (!phoneDigits) e.user_phone = t('GENERAL.PHONE_NUMBER_IS_REQUIRED');

		if (showApprovedDomainsInput && !approvedDomainsFieldDisabled) {
			const domainRows = (approvedDomainRows || []).map((value) => String(value || '').trim());
			if (domainRows.some((value) => !value)) {
				e.approved_domains = "One or more domain fields are empty. Delete unused rows or enter a domain.";
			} else {
				const domains = normalizeDomainCsv(local.approved_domains);
				if (domains.length === 0) {
					e.approved_domains = "Approved domains are required for the selected role.";
				} else {
					const invalid = domains.find((d) => !isValidDomain(d));
					if (invalid) {
						e.approved_domains = `Invalid domain: ${invalid}`;
					} else if (creatorAllowedDomains.length > 0) {
						const outsideScope = domains.find((d) => !creatorAllowedDomains.includes(d));
						if (outsideScope) e.approved_domains = `Domain outside your allowed scope: ${outsideScope}`;
					}
				}
			}
		}
		if (showApprovedDomainsInput) {
			const domainRows = (approvedDomainRows || []).map((value) => String(value || '').trim());
			if (domainRows.some((value) => !value)) {
				e.approved_domains = "One or more domain fields are empty. Delete unused rows or enter a domain.";
			} else {
				const domains = normalizeDomainCsv(local.approved_domains);
				if (domains.length === 0) {
				e.approved_domains = "Approved domains are required for the selected role.";
				} else {
					const invalid = domains.find((d) => !isValidDomain(d));
					if (invalid) {
						e.approved_domains = `Invalid domain: ${invalid}`;
					} else if (creatorAllowedDomains.length > 0) {
						const outsideScope = domains.find((d) => !creatorAllowedDomains.includes(d));
						if (outsideScope) {
							e.approved_domains = `Domain outside your allowed scope: ${outsideScope}`;
						}
					}
				}
			}
		}
			if (!sendPasswordEmail && !isEdit) {
				const p = passwords.pass, c = passwords.cpass;
				const minLength = p.length >= 8;
			const uppercase = /[A-Z]/.test(p);
			const specialChar = /[!@#$%^&*]/.test(p);
			const number = /[0-9]/.test(p);
			const confirmpass = p === c;
			if (!(minLength && uppercase && specialChar && number && confirmpass)) e.passwords = t('GENERAL.PASSWORD_REQUIREMENTS_NOT_MET');
		}
		setErrors(e);
		return e;
	};

	const getValidationAlertHtml = (validationErrors = {}) => {
		const fieldLabels = {
			user_first_name: 'First Name', user_last_name: 'Last Name', user_email: 'Email',
			user_phone: 'Phone Number', company_name: 'Company Name', region: 'Region',
			country: 'Country', user_role: 'Role', user_status: 'Status',
			approved_domains: 'Approved Domains', passwords: 'Password',
		};
		const orderedKeys = ['user_first_name', 'user_last_name', 'user_email', 'user_phone',
			'company_name', 'region', 'country', 'user_role', 'user_status', 'approved_domains', 'passwords'];
		const messages = orderedKeys
			.filter((key) => validationErrors?.[key])
			.map((key) => `<li><strong>${fieldLabels[key] || key}:</strong> ${validationErrors[key]}</li>`);
		if (messages.length === 0) return t('UI_TEXT.PLEASE_FIX_THE_HIGHLIGHTED_FIELDS');
		return `<div style="text-align:center">Please correct the following<ul style="margin:8px 0 0 18px;padding:0;">${messages.join('')}</ul></div>`;
	};

	const buildAllocations = () => {
		const siteToCustomerMap = new Map();
		(allSites || []).forEach((site) => {
			const siteId = Number(site?.site_id);
			const cusId = Number(site?.cus_id);
			if (
				Number.isFinite(siteId) &&
				siteId > 0 &&
				Number.isFinite(cusId) &&
				cusId > 0 &&
				!siteToCustomerMap.has(siteId)
			) {
				siteToCustomerMap.set(siteId, cusId);
			}
		});
		const dedupe = new Set();
		const arr = [];
		(assignedSitesLoc || []).forEach((entry) => {
			const entryCustomerId = Number(entry?.customer?.cus_id ?? entry?.customer);
			(entry?.sites || []).forEach((sid) => {
				const siteId = Number(sid?.site_id ?? sid);
				if (!Number.isFinite(siteId) || siteId <= 0) return;
				const mappedCustomerId = siteToCustomerMap.get(siteId);
				const customerId = Number.isFinite(entryCustomerId) && entryCustomerId > 0
					? entryCustomerId
					: mappedCustomerId;
				if (!Number.isFinite(customerId) || customerId <= 0) return;
				const key = `${customerId}:${siteId}`;
				if (dedupe.has(key)) return;
				dedupe.add(key);
				arr.push({ cus_id: customerId, site_id: siteId });
			});
		});

		if (!isEdit) return arr;

		const explicitAllSitesMode = canUseGlobalAllSitesMode && !local?._overrideAlloc;
		if (explicitAllSitesMode) return [];

		if (arr.length > 0) return arr;

		return existingAllocations;
	};

	// const checkDuplicateEmailBeforeSubmit = async () => {
	// 	const normalizedEmail = normalizeEmail(local?.user_email || "");
	// 	if (!normalizedEmail || !isValidEmailFormat(normalizedEmail)) return false;
	// 	if (isEdit && normalizedEmail === initialEmailNormalized) return false;
	// 	const resp = await checkUserEmailExists(normalizedEmail);
	// 	return Boolean(resp?.status && resp?.data?.exists === true);
	// };
	const checkDuplicateEmailBeforeSubmit = async () => {
		const normalizedEmail = normalizeEmail(local?.user_email || "");
		if (!normalizedEmail || !isValidEmailFormat(normalizedEmail)) return false;
		if (isEdit && normalizedEmail === initialEmailNormalized) return false;
		const resp = await checkUserEmailExists(normalizedEmail);
		return Boolean(resp?.status && resp?.data?.exists === true);
	};

	const handleSave = async () => {
		const validationErrors = validate();
		if (Object.keys(validationErrors).length > 0) {
			Swal.fire({ icon: 'error', title: t('UI_TEXT.VALIDATION_ERROR'), html: getValidationAlertHtml(validationErrors) });
			return;
		}
		try {
			const duplicateEmailExists = await checkDuplicateEmailBeforeSubmit();
			if (duplicateEmailExists) {
				setEmailExists(true);
				setErrors((prev) => ({ ...prev, user_email: 'Email already exists. Please use a different email.' }));
				Swal.fire({ icon: 'error', title: t('UI_TEXT.VALIDATION_ERROR'), text: 'Email already exists. Please use a different email.' });
				return;
			}
		} catch (_err) { /* backend enforces uniqueness */ }

		setLoading(true);
		try {
			const allocations = buildAllocations();
			const phone_country = resolvePhoneCountry({ iso2: local.phone_country_iso2, dialCode: local.phone_country_code, label: local.country });
			const phoneMeta = getPhoneMetaForCountry(phone_country);
			const user_meta = {
				company_name: local.company_name,
				region: local.region,
				country: local.country,
				approved_domains: (() => {
					if (!showApprovedDomainsInput || approvedDomainsFieldDisabled) {
						if (isEdit) return String(userData?.approved_domains || userData?.user_meta?.approved_domains || local.approved_domains || "");
						return String(local.approved_domains || "");
					}
					return String(local.approved_domains || "");
				})(),
				phone_country_code: phoneMeta.phone_country_code,
				phone_country_iso2: phoneMeta.phone_country_iso2,
				phone_country_flag: phoneMeta.phone_country_flag,
				notify_order_creations_and_requests: !!local.notify_order_creations_and_requests,
				...(companyExtras?.stateProvince ? { company_state_province: companyExtras.stateProvince } : {}),
				...(companyExtras?.streetAddress ? { company_street_address: companyExtras.streetAddress } : {}),
				...(companyExtras?.city ? { company_city: companyExtras.city } : {}),
				...(companyExtras?.zipPostalCode ? { company_zip_postal_code: companyExtras.zipPostalCode } : {}),
			};

			if (!isEdit) {
				const body = {
					user_first_name: local.user_first_name,
					user_last_name: local.user_last_name,
					user_email: local.user_email,
					user_phone: `${local.user_phone}`.trim(),
					user_role: selectedRole?.role_id ?? local.user_role,
					user_status: local.user_status,
					user_display_name: `${local.user_first_name} ${local.user_last_name}`,
					send_password_email: !!sendPasswordEmail,
					allocations,
					user_meta,
				};
				if (!sendPasswordEmail) {
					body.user_password = passwords.pass;
					body.confirm_password = passwords.cpass;
				}
				const resp = await createNewUser(body);
				if (resp?.status) {
					Swal.fire({ icon: 'success', title: 'User Created Successfully!' });
					onSaved?.();
					onClose?.();
				} else {
					Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to create user' });
				}
			} else {
				const { user_id } = userData || {};
				const data = {
					user_first_name: local.user_first_name,
					user_last_name: local.user_last_name,
					user_email: local.user_email,
					user_phone: `${local.user_phone}`.trim(),
					user_role: selectedRole?.role_id ?? local.user_role,
					user_status: local.user_status,
					user_meta,
					allocations,
				};
				const resp = await updateUser({ userId: user_id, data });
				if (resp?.status) {
					Swal.fire({ icon: 'success', title: t('ALERT_MESSAGES.SUCCESS'), text: t('UI_TEXT.USER_SUCCESSFULLY_UPDATED') });
					if (isSelf) {
						updateUserStatus?.({
							user_first_name: data.user_first_name,
							user_last_name: data.user_last_name,
							user_email: data.user_email,
							user_phone: data.user_phone,
							user_status: data.user_status,
							user_meta,
							...data,
							...user_meta,
							 allocations
						});
						if (typeof fetchSites === "function") {
							try { await fetchSites(); } catch (error) { console.error("Failed to refresh sites after self-update", error); }
						}
					}
					onSaved?.({ userId: user_id, data, user_meta, allocations });
					onClose?.();
				} else {
					Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update user' });
				}
			}
		} catch (err) {
			console.error(err);
			Swal.fire({ icon: 'error', title: 'Error', text: err.message });
		} finally {
			setLoading(false);
		}
	};

	if (rolesLoading) {
		return (
			<Paper elevation={1} sx={{ p: 3, borderRadius: 2 }} className="user-edit-form" ref={contref}>
				<Box display="flex" alignItems="center" justifyContent="center" sx={{ minHeight: 240, gap: 2 }}>
					<CircularProgress size={24} />
					<Typography variant="body1">{t('UI_TEXT.LOADING_USER_FORM')}</Typography>
				</Box>
			</Paper>
		);
	}

	return (
		<Paper elevation={1} sx={{ p: 3, borderRadius: 2, mt: 2 }} className="user-edit-form" ref={contref}>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
				<Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', height: '100%' }}>
					<Typography variant="h6">{isEdit ? t('UI_TEXT.EDIT_USER') : t('UI_TEXT.CREATE_NEW_USER')}</Typography>
					{isEdit && local.user_email ? (
						<Typography variant="body1" color="text.secondary" sx={{ mb: 0.75 }}>
							Email: {local.user_email}
						</Typography>
					) : (
						<Typography variant="body1" color="text.secondary" sx={{ mb: 0.75 }}>
							{t('UI_TEXT.PLEASE_FILL_IN_BELOW_DETAILS_TO_CREATE_NEW_USER')}
						</Typography>
					)}
				</Box>
				<Box display="flex" justifyContent="flex-end" alignItems="center" columnGap={1}>
					<Button variant="outlined" color="primary" onClick={onClose}>{t('BUTTON.CANCEL')}</Button>
					{isEdit && (
						<Button variant="contained" startIcon={<VpnKeyRounded />} onClick={() => setPwdModalOpen(true)}>
							{t('TITLES.RESET_USER_PASSWORD')}
						</Button>
					)}
				</Box>
			</Box>

			<div className="edit-user-form">
				<Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1, alignItems: 'stretch', columnGap: 3 }} data-tour="admin-create-user-details">
					{/* Personal Details */}
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography variant="body1" sx={{ fontWeight: 700 }}>{t('DIVIDER.USER_DETAILS') || 'Personal Details'}</Typography>
						<Box sx={{ mt: 1 }}>
							<Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
								<Box sx={{ flex: 1 }}>
									<TextField
										label={t('LABELS.FIRST_NAME')}
										fullWidth size="small" margin="normal" required
										value={local.user_first_name}
										error={Boolean(errors.user_first_name)} helperText={errors.user_first_name || ''}
										onChange={(e) => setLocal({ ...local, user_first_name: e.target.value })}
									/>
								</Box>
								<Box sx={{ flex: 1 }}>
									<TextField
										label={t('LABELS.LAST_NAME')}
										fullWidth size="small" margin="normal" required
										value={local.user_last_name}
										error={Boolean(errors.user_last_name)} helperText={errors.user_last_name || ''}
										onChange={(e) => setLocal({ ...local, user_last_name: e.target.value })}
									/>
								</Box>
							</Box>
							<TextField
								label={t('LABELS.EMAIL')} fullWidth size="small" margin="normal" required
								value={local.user_email}
								error={Boolean(errors.user_email) || emailExists}
								helperText={
									errors.user_email ||
									(emailExists ? 'Email already exists. Please use a different email.' :
										(emailCheckInProgress ? 'Checking email availability...' : ''))
								}
								onChange={(e) => {
									setLocal({ ...local, user_email: e.target.value });
									if (errors.user_email) setErrors((prev) => ({ ...prev, user_email: '' }));
								}}
							/>
							<Box mt={2}>
								<PhoneInput
									value={local.user_phone}
									countryIso2={local.phone_country_iso2}
									countryLabel={local.country}
									countryCode={local.phone_country_code}
									onChange={(value) => {
										setLocal((prev) => ({ ...prev, user_phone: value }));
										setErrors((prev) => ({ ...prev, user_phone: "" }));
									}}
									onCountryChange={(country) => {
										const phoneMeta = getPhoneMetaForCountry(country);
										setLocal((prev) => ({
											...prev,
											user_phone: normalizePhoneForCountry(prev.user_phone || "", country),
											...phoneMeta,
										}));
									}}
									label={t('LABELS.PHONE')}
									required
									size="small"
									helperText={errors.user_phone || ""}
									error={Boolean(errors.user_phone)}
								/>
							</Box>

							{!isEdit && (
								<FormControlLabel
									sx={{ mt: 1 }}
									control={<Switch checked={sendPasswordEmail} onChange={(e) => setSendPasswordEmail(e.target.checked)} />}
									label={t('GENERAL.SEND_PASSWORD_CREATION_EMAIL')}
								/>
							)}
							{!isEdit && !sendPasswordEmail && (
								<>
									<TextField label={t('LABELS.PASSWORD')} type="password" fullWidth size="small" margin="normal"
										value={passwords.pass}
										onChange={(e) => {
											const p = e.target.value;
											setPasswords({ ...passwords, pass: p });
											const minLength = p.length >= 8;
											const uppercase = /[A-Z]/.test(p);
											const specialChar = /[!@#$%^&*]/.test(p);
											const number = /[0-9]/.test(p);
											setPwdValidation({ ...pwdValidation, minLength, uppercase, specialChar, number, confirmpass: p === passwords.cpass });
										}}
									/>
									<TextField label={t('LABELS.CONFIRM_PASSWORD')} type="password" fullWidth size="small" margin="normal"
										value={passwords.cpass}
										onChange={(e) => {
											const c = e.target.value;
											setPasswords({ ...passwords, cpass: c });
											setPwdValidation({ ...pwdValidation, confirmpass: passwords.pass === c });
										}}
										error={passwords.cpass !== '' && passwords.pass !== passwords.cpass}
										helperText={passwords.cpass !== '' && passwords.pass !== passwords.cpass ? t('ALERT_MESSAGES.PASSWORDS_DO_NOT_MATCH') : ''}
									/>
									<Box sx={{ mb: 1 }}>
										{[
											[pwdValidation.minLength, t('UI_TEXT.MINIMUM_EIGHT_CHARACTERS')],
											[pwdValidation.uppercase, t('UI_TEXT.AT_LEAST_ONE_UPPERCASE')],
											[pwdValidation.specialChar, t('UI_TEXT.AT_LEAST_ONE_SPECIAL_CHARACTERS')],
											[pwdValidation.number, t('UI_TEXT.AT_LEAST_ONE_NUMBER')],
											[pwdValidation.confirmpass, t('UI_TEXT.CONFIRM_PASSWORD_MATCHES')],
										].map(([ok, label], i) => (
											<Typography key={i} variant="caption" sx={{ display: 'block', color: ok ? 'green' : 'text.secondary' }}>
												{ok ? <CheckCircleIcon fontSize="inherit" /> : <ErrorOutlineIcon fontSize="inherit" />} {label}
											</Typography>
										))}
									</Box>
								</>
							)}
						</Box>
					</Box>

					<Box sx={{ display: { xs: 'none', md: 'block' } }}>
						<Divider orientation="vertical" flexItem sx={{ height: '100%' }} />
					</Box>

					{/* Company Details */}
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography variant="body1" sx={{ fontWeight: 700 }}>{t('TITLES.COMPANY_DETAILS')}</Typography>
						<Box sx={{ mt: 1 }}>
							<TextField
								label={t('LABELS.COMPANY_NAME')} fullWidth size="small" margin="normal" required
								value={local.company_name}
								error={Boolean(errors.company_name)} helperText={errors.company_name || ''}
								onChange={(e) => setLocal({ ...local, company_name: e.target.value })}
							/>
                            <Autocomplete
								options={REGION_OPTIONS}
								value={local.region || null}
								onChange={(_, v) => setLocal({ ...local, region: v || '', country: '' })}
								autoHighlight selectOnFocus handleHomeEndKeys openOnFocus
								renderInput={(params) => (
									<TextField {...params} label={t('LABELS.REGION')} required error={Boolean(errors.region)} helperText={errors.region || ''} size="small" margin="normal" />
								)}
							/>
							<CountryAutocomplete
								key={local.region}
								options={countries}
								value={local.country || ""}
								onChange={(nextCountryLabel) =>
									setLocal((prev) => {
										const nextPhoneCountry = resolvePhoneCountry({ label: nextCountryLabel });
										const nextPhoneMeta = getPhoneMetaForCountry(nextPhoneCountry);
										return {
											...prev,
											country: nextCountryLabel,
											user_phone: normalizePhoneForCountry(prev.user_phone || "", nextPhoneCountry),
											...nextPhoneMeta,
										};
									})
								}
								label={t('LABELS.COUNTRY')}
								required
								error={Boolean(errors.country)}
								helperText={errors.country || ""}
								disabled={!local.region}
							/>
							<Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
								<Box sx={{ flex: 1 }}>
									<TextField label={t('LABELS.STATE_PROVINCE')} fullWidth size="small" margin="normal"
										value={companyExtras.stateProvince}
										onChange={(e) => setCompanyExtras({ ...companyExtras, stateProvince: e.target.value })}
									/>
								</Box>
								<Box sx={{ flex: 1 }}>
									<TextField label={t('LABELS.CITY')} fullWidth size="small" margin="normal"
										value={companyExtras.city}
										onChange={(e) => setCompanyExtras({ ...companyExtras, city: e.target.value })}
									/>
								</Box>
							</Box>
							<Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
								<Box sx={{ flex: 2 }}>
									<TextField label={t('LABELS.STREET_ADDRESS')} fullWidth size="small" margin="normal"
										value={companyExtras.streetAddress}
										onChange={(e) => setCompanyExtras({ ...companyExtras, streetAddress: e.target.value })}
									/>
								</Box>
								<Box sx={{ flex: 1 }}>
									<TextField label={t('LABELS.ZIP_POSTAL_CODE')} fullWidth size="small" margin="normal"
										value={companyExtras.zipPostalCode}
										onChange={(e) => setCompanyExtras({ ...companyExtras, zipPostalCode: e.target.value })}
									/>
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>

				{/* Status and Role */}
				<Typography variant="body1" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>{t('UI_TEXT.USER_STATUS_ROLE')}</Typography>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, rowGap: 0, mt: 2 }}>
					<Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
						<Autocomplete
							options={assignableRoleOptions}
							groupBy={(option) => option?.role_type || "Other"}
							getOptionLabel={(option) => option?.role_name || ""}
							isOptionEqualToValue={(option, value) => Number(option?.role_id) === Number(value?.role_id)}
							value={selectedRole || null}
							onChange={(_, value) => setLocal({ ...local, user_role: value ? String(value.role_id) : "" })}
							disabled={isSelf}
							renderGroup={(params) => (
								<li key={params.key}>
									<GroupHeader>Role Type: {params.group}</GroupHeader>
									<GroupItems>{params.children}</GroupItems>
								</li>
							)}
							renderInput={(params) => (
								<TextField {...params} label={t('LABELS.ROLE')} required size="small" error={Boolean(errors.user_role)} helperText={errors.user_role || ''} />
							)}
						/>
					</Box>
					<Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
						<FormControl variant="outlined" fullWidth size="small" required>
							<InputLabel>{t('TABLE_HEADERS.STATUS')}</InputLabel>
							<Select
								label={t('TABLE_HEADERS.STATUS')}
								value={local.user_status}
								onChange={(e) => setLocal({ ...local, user_status: e.target.value })}
								error={Boolean(errors.user_status)}
								disabled={isSelf}
							>
								<MenuItem value="1">{t('MENU_ITEMS.APPROVED')}</MenuItem>
								<MenuItem value="0">{t('MENU_ITEMS.PENDING')}</MenuItem>
							</Select>
							{errors.user_status && <FormHelperText error>{errors.user_status}</FormHelperText>}
						</FormControl>
					</Box>

					<Box sx={{ width: '100%' }}>
						<Alert severity="info" sx={{ mt: 1.5 }}>
							<AlertTitle>{`Role: ${selectedRole?.role_name || "Not selected"}`}</AlertTitle>
							{roleGuidanceText}
						</Alert>
					</Box>
				</Box>

				{/* Customer Location Allocation */}
				<Box sx={{ width: '100%', mt: 2 }} data-tour="admin-create-user-customer-allocation">
					<Typography variant="body1" sx={{ fontWeight: 700, mb: 0.5 }}>{t('UI_TEXT.CUSTOMER_LOCATION_ALLOCATION')}</Typography>
					{allocNoteText && (
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{allocNoteText}</Typography>
					)}
					{showAllocateSpecificLocationsSection && (
						<Box sx={{ mt: 1, p: 2, borderRadius: "10px", bgcolor: '#fafafa', display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
							<Typography variant="body2" fontSize={"11px"} color="text.secondary">
								{local?._overrideAlloc ? (
									<>{t('UI_TEXT.ASSIGNING_SPECIFIC_LOCATIONS')}<br />{t('UI_TEXT.CANCEL_ALLOCATIONS_TO_ALLOCATE_ALL')}</>
								) : (
									<>{t('UI_TEXT.ADMIN_ACCESS_ALL_LOCATIONS')}<br />{t('UI_TEXT.ALLOCATE_SPECIFIC_LOCATIONS_BUTTON')}</>
								)}
							</Typography>
							{canUseGlobalAllSitesMode && (
								<Button
									variant={local?._overrideAlloc ? "outlined" : "contained"}
									onClick={() => {
										const next = !local?._overrideAlloc;
										setLocal((prev) => ({ ...prev, _overrideAlloc: next }));
										onAssignedSitesChange?.([]);
									}}
								>
									{local?._overrideAlloc ? t('UI_TEXT.CANCEL_ALLOCATIONS') : t('UI_TEXT.ALLOCATE_SPECIFIC_LOCATIONS')}
								</Button>
							)}
						</Box>
					)}
					{(!canUseGlobalAllSitesMode || local?._overrideAlloc) && (
						<div className="user-customer-section" style={{ marginTop: '10px' }}>
							{!actorManageAllSites && actorScopeLoading && (
								<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
									Loading your assigned customer-site scope...
								</Typography>
							)}
							{!actorManageAllSites && !actorScopeLoading && effectiveActorSiteIds.length === 0 && (
								<Typography variant="body2" color="error" sx={{ mb: 1 }}>
									You do not have any assigned sites. Contact a super admin to assign at least one site.
								</Typography>
							)}
							{/* ── KEY FIX: pass groupedAssignedSitesForListBoxes instead of assignedSitesLoc ── */}
							<Listboxes
								sites={scopedSites}
								customers={scopedCustomers}
								assignedSitesProp={groupedAssignedSitesForListBoxes}
								onAssignedSitesChange={onAssignedSitesChange}
							/>
						</div>
					)}
				</Box>

				{/* Admin Notifications Toggle */}
				{selectedRoleIsAdminType && (
					<Box sx={{ mt: 3 }}>
						<Typography variant="body1" sx={{ fontWeight: 700, mb: 0.5 }}>{"Order Creation & Request List Notification Preference"}</Typography>
						<FormControlLabel
							control={
								<Switch
									checked={!!local.notify_order_creations_and_requests}
									onChange={(e) => setLocal({ ...local, notify_order_creations_and_requests: e.target.checked })}
								/>
							}
							label={t('UI_TEXT.RECEIVE_NOTIFICATIONS_ORDER_CREATIONS_REQUEST_LIST')}
						/>
					</Box>
				)}
			</div>

			<div className="button-container">
				<Button
					variant="contained" color="primary" onClick={handleSave}
					disabled={(() => {
						const phoneDigits = getNationalDigits(local.user_phone || '', local.phone_country_iso2 || 'US');
						const requiredOk = !!(local.user_first_name && local.user_last_name && local.user_email && local.company_name && local.region && local.country && local.user_role && (local.user_status !== '' && local.user_status !== undefined && local.user_status !== null) && phoneDigits);
						const allocNeeded = !(canUseGlobalAllSitesMode && !local?._overrideAlloc);
						const hasAnyAlloc = (assignedSitesLoc || []).length > 0;
						const allocOk = allocNeeded ? hasAnyAlloc : true;
						if (!isEdit && !sendPasswordEmail) {
							const p = passwords.pass, c = passwords.cpass;
							const minLength = p.length >= 8; const uppercase = /[A-Z]/.test(p); const specialChar = /[!@#$%^&*]/.test(p); const number = /[0-9]/.test(p); const match = p === c;
							if (!(minLength && uppercase && specialChar && number && match)) return true;
						}
						return loading || !requiredOk || !allocOk || !domainOk || emailExists || emailCheckInProgress;
					})()}
					sx={{ mt: 2 }}
				>
					{loading
						? (<><CircularProgress size={20} sx={{ color: 'white', mr: 1 }} /> {isEdit ? t('BUTTON.SAVING') : t('BUTTON.CREATING')}</>)
						: (isEdit ? t('BUTTON.SAVE_CHANGES') : t('BUTTON.CREATE_USER'))
					}
				</Button>
				<Button variant="outlined" color="primary" onClick={onClose} sx={{ mt: 2, ml: 1 }}>{t('BUTTON.CANCEL')}</Button>
			</div>
			<ResetPasswordModal open={pwdModalOpen} onClose={() => setPwdModalOpen(false)} userId={userData?.user_id} />
		</Paper>
	);
};

export default CreateEditUserForm;
